import { ResourceContext } from '@seddi/core';

/**
 * DOM raw image resource.
 */
export type DomImage = {
  id: string;
  channels: number;
  data: Uint8ClampedArray;
  sh: number;
  sw: number;
};

/**
 * DOM raw image resource context.
 */
export class DomImagesContext extends ResourceContext<DomImage> {
  /** Hidden canvas used for rasterization. */
  private canvas: HTMLCanvasElement;
  /** Hidden canvas context used for rasterization. */
  private canvasCtx: CanvasRenderingContext2D;

  /**
   * Creates a new context.
   */
  constructor() {
    super();
    this.canvas = document.createElement('canvas');
    this.canvasCtx = this.canvas.getContext('2d', { willReadFrequently: true });
  }

  /**
   * Loads the image.
   * If the image is already loaded, returns a resolved promise.
   * If the image is currently being loaded, returns a promise that will be resolved when the image finishes loading.
   * @param id Image id.
   * @param url Image url.
   */
  loadEx(id: string, url: string): Promise<void> {
    return super.load(
      id,
      () =>
        new Promise(resolve => {
          // dom image creation
          const img = document.createElement('img');
          img.setAttribute('crossOrigin', 'Anonymous');
          img.style.display = 'none';
          img.onload = () => {
            // use canvas to extract raw data
            const { canvas, canvasCtx } = this;
            canvas.width = img.naturalWidth;
            canvas.height = img.naturalHeight;
            canvasCtx.drawImage(img, 0, 0);
            const data = canvasCtx.getImageData(
              0,
              0,
              img.naturalWidth,
              img.naturalHeight,
            ).data;

            const resource: DomImage = {
              id,
              channels: 4,
              data,
              sh: img.naturalHeight,
              sw: img.naturalWidth,
            };
            resolve(resource);

            // dom image disppose
            img.remove();
          };

          img.src = url;
        }),
    );
  }
}
